import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { connect } from "react-redux"
import { motion, AnimatePresence } from "framer-motion"
import styles from "./WorkOfferById.module.scss"
import { getWorkOfferById, sendWorkOfferPostulation, resetWorkOfferPostulation } from "../../store/actions"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate } from "react-router-dom"
import Logo from "./../../assets/images/logo-navbar.png"
import WorkApplyModal from "./WorkApplyModal.tsx"
import {
    User,
    Phone,
    Mail,
    FileText,
    Upload,
    Send,
    CheckCircle,
    Facebook,
    Twitter,
    Linkedin,
    Instagram,
    Briefcase,
    MapPin,
    Calendar,
    DollarSign,
    Clock,
} from "lucide-react"

const pageVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { duration: 0.5, ease: "easeInOut" },
    },
    exit: { opacity: 0, transition: { duration: 0.3 } },
}

const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: 0.2 },
    },
}

const successAnimation = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            duration: 0.6,
            ease: "easeOut",
        },
    },
}

const WorkOfferById = ({
    workOffer,
    getWorkOfferById,
    sendWorkOfferPostulation,
    workOfferPostulation,
    resetWorkOfferPostulation,
}) => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [showApplyModal, setShowApplyModal] = useState(true)
    const [formattedSalary, setFormattedSalary] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [selectedFileName, setSelectedFileName] = useState("")

    useEffect(() => {
        getWorkOfferById(id)
        resetWorkOfferPostulation()
    }, [id, getWorkOfferById ])

    useEffect(() => {
        if (workOffer?.salary) {
            const salary = workOffer?.salary ? Math.floor(Number.parseFloat(workOffer?.salary)) : "0"

            if (workOffer?.salary === "0.00") {
                setFormattedSalary("A convenir")
            } else if (workOffer?.salaryType === "Hour") {
                setFormattedSalary(`$${salary}/hora`)
            } else if (workOffer?.salaryType === "Day") {
                setFormattedSalary(`$${salary}/día`)
            } else if (workOffer?.salaryType === "Week") {
                setFormattedSalary(`$${salary}/semana`)
            } else if (workOffer?.salaryType === "Month") {
                setFormattedSalary(`$${salary}/mes`)
            } else if (workOffer?.salaryType === "Job") {
                setFormattedSalary(`$${salary}/proyecto`)
            }
        }
    }, [workOffer])

    const formatStartDate = (date) => {
        if (!date) return ""
        const day = date?.split("T")[0].split("-")[2]
        const month = date?.split("T")[0].split("-")[1]
        const year = date?.split("T")[0].split("-")[0]
        return `${day}/${month}/${year}`
    }

    const formatwokZoneCityname = (workZone) => {
        if (!workZone) return ""
        return workZone.split(",")[0]
    }

    const formik = useFormik({
        initialValues: {
            jobRequestId: Number.parseInt(id),
            email: "",
            name: "",
            phone: "",
            cv: "",
            coverLetter: "",
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Email inválido").required("Campo requerido"),
            name: Yup.string().required("Campo requerido"),
            phone: Yup.string()
                .matches(/^(\d{3})?[-.\s]?\d{3}[-.\s]?\d{4}$/, "Teléfono inválido")
                .required("Campo requerido"),
        }),
        onSubmit: (values) => {
            setIsSubmitting(true)
            sendWorkOfferPostulation(values)
            setTimeout(() => {
                setIsSubmitting(false)
            }, 1000)
        },
    })

    const handleFileChange = (event) => {
        const file = event.currentTarget.files[0]
        if (file) {
            setSelectedFileName(file.name)
            formik.setFieldValue("cv", file)
        }
    }

    const handleRedirect = () => {
        navigate("/register")
        resetWorkOfferPostulation()
    }

    if (!workOffer) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loader}></div>
                <p>Cargando oferta de trabajo...</p>
            </div>
        )
    }

    const jobOfferUser = workOffer?.user
    const jobOfferUserFullName =
        jobOfferUser?.type === "COMPANY" ? jobOfferUser?.razon_social : `${jobOfferUser?.name} ${jobOfferUser?.last_name}`

    const isExternalOffer = jobOfferUser?.email?.toLowerCase() === "gonzao@edify.la"

    return (
        <motion.main className={styles.container} initial="hidden" animate="visible" exit="exit" variants={pageVariants}>
            <div className={styles.header}>
                    <img src={Logo} alt="Edify Logo" className={styles.header_logo} />
                </div>
            {showApplyModal && (
                <WorkApplyModal setShowApplyModal={setShowApplyModal} workOffer={workOffer} forbiddenClose={true} />
            )}

            <div className={styles.contentWrapper}>
                <AnimatePresence mode="wait">
                    {workOfferPostulation && (
                        <motion.div
                            key="success"
                            className={styles.successContainer}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            variants={pageVariants}
                        >
                            <motion.div className={styles.successIcon} variants={successAnimation}>
                                <CheckCircle size={80} />
                            </motion.div>

                            <h2 className={styles.successTitle}>¡Tu postulación ha sido enviada con éxito!</h2>
                            <p className={styles.successSubtitle}>Hemos enviado toda tu información al empleador</p>

                            <div className={styles.joinUsSection}>
                                <h3 className={styles.joinUsTitle}>¿Querés formar parte de nuestra comunidad?</h3>
                                <button className={styles.registerButton} onClick={handleRedirect}>
                                    Registrate
                                </button>
                            </div>

                            <div className={styles.socialSection}>
                                <h4 className={styles.socialTitle}>Seguinos en:</h4>
                                <div className={styles.socialIcons}>
                                    <a
                                        href="https://www.facebook.com/p/Edify-100063884114544/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.socialLink}
                                    >
                                        <Facebook size={24} />
                                    </a>
                                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
                                        <Twitter size={24} />
                                    </a>
                                    <a
                                        href="https://ar.linkedin.com/company/edifyargentina"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.socialLink}
                                    >
                                        <Linkedin size={24} />
                                    </a>
                                    <a
                                        href="https://www.instagram.com/edify.la/?hl=es-la"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.socialLink}
                                    >
                                        <Instagram size={24} />
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    )}
                    {!workOfferPostulation && (
                        <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="exit" 
                            key="form" 
                            className={styles.offerContainer} 
                            variants={pageVariants}>
                            <div className={styles.offerHeader}>
                                <div className={styles.companyInfo}>
                                    <img
                                        src={jobOfferUser?.image_profile || "/placeholder.svg"}
                                        alt="Logo de la empresa"
                                        className={styles.companyLogo}
                                    />
                                    <div className={styles.companyDetails}>
                                        <h1 className={styles.companyName}>{jobOfferUserFullName}</h1>
                                        {isExternalOffer && <span className={styles.externalBadge}>Oferta Externa</span>}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.offerContent}>
                                <div className={styles.offerSection}>
                                    <h2 className={styles.sectionTitle}>
                                        <Briefcase className={styles.sectionIcon} />
                                        Rubros requeridos
                                    </h2>
                                    <div className={styles.skillsList}>
                                        {workOffer.skill?.map((skill, index) => (
                                            <span key={index} className={styles.skillTag}>
                                                {skill?.skill?.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {workOffer?.description && workOffer.description.trim() !== "" && (
                                    <div className={styles.offerSection}>
                                        <h2 className={styles.sectionTitle}>
                                            <FileText className={styles.sectionIcon} />
                                            Descripción de tareas
                                        </h2>
                                        <p className={styles.descriptionText}>{workOffer.description}</p>
                                    </div>
                                )}

                                <div className={styles.offerSection}>
                                    <h2 className={styles.sectionTitle}>
                                        <MapPin className={styles.sectionIcon} />
                                        Detalles de la oferta
                                    </h2>
                                    <div className={styles.detailsGrid}>
                                        {workOffer?.workZones?.map((workZone, index) => (
                                            <div key={index} className={styles.detailItem}>
                                                <MapPin className={styles.detailIcon} />
                                                <span className={styles.detailText}>
                                                    {workZone?.jurisdiccion?.name} - {formatwokZoneCityname(workZone?.city)}
                                                </span>
                                            </div>
                                        ))}

                                        <div className={styles.detailItem}>
                                            <Calendar className={styles.detailIcon} />
                                            <span className={styles.detailText}>
                                                {workOffer?.immediateStart
                                                    ? "Inicio inmediato"
                                                    : `Inicia ${formatStartDate(workOffer?.startDate)}`}
                                            </span>
                                        </div>

                                        <div className={styles.detailItem}>
                                            <DollarSign className={styles.detailIcon} />
                                            <span className={styles.detailText}>{formattedSalary}</span>
                                        </div>

                                        <div className={styles.detailItem}>
                                            <Clock className={styles.detailIcon} />
                                            <span className={styles.detailText}>Hasta {formatStartDate(workOffer?.activeUntil)}</span>
                                        </div>
                                    </div>
                                </div>

                                <motion.div className={styles.formSection} variants={formVariants}>
                                    <h2 className={styles.sectionTitle}>
                                        <Send className={styles.sectionIcon} />
                                        Aplicar a esta oferta
                                    </h2>

                                    <form className={styles.applicationForm} onSubmit={formik.handleSubmit}>
                                        <div className={styles.formGrid}>
                                            <div className={styles.formGroup}>
                                                <label htmlFor="name" className={styles.formLabel}>
                                                    Nombre completo
                                                </label>
                                                <div className={styles.inputWrapper}>
                                                    <User className={styles.inputIcon} />
                                                    <input
                                                        id="name"
                                                        name="name"
                                                        type="text"
                                                        className={`${styles.formInput} ${formik.touched.name && formik.errors.name ? styles.inputError : ""
                                                            }`}
                                                        placeholder="Tu nombre completo"
                                                        value={formik.values.name}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                    />
                                                </div>
                                                {formik.touched.name && formik.errors.name && (
                                                    <div className={styles.errorMessage}>{formik.errors.name}</div>
                                                )}
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label htmlFor="phone" className={styles.formLabel}>
                                                    Teléfono
                                                </label>
                                                <div className={styles.inputWrapper}>
                                                    <Phone className={styles.inputIcon} />
                                                    <input
                                                        id="phone"
                                                        name="phone"
                                                        type="tel"
                                                        className={`${styles.formInput} ${formik.touched.phone && formik.errors.phone ? styles.inputError : ""
                                                            }`}
                                                        placeholder="(011) 1234-5678"
                                                        value={formik.values.phone}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                    />
                                                </div>
                                                {formik.touched.phone && formik.errors.phone && (
                                                    <div className={styles.errorMessage}>{formik.errors.phone}</div>
                                                )}
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label htmlFor="email" className={styles.formLabel}>
                                                    Email
                                                </label>
                                                <div className={styles.inputWrapper}>
                                                    <Mail className={styles.inputIcon} />
                                                    <input
                                                        id="email"
                                                        name="email"
                                                        type="email"
                                                        className={`${styles.formInput} ${formik.touched.email && formik.errors.email ? styles.inputError : ""
                                                            }`}
                                                        placeholder="tu@email.com"
                                                        value={formik.values.email}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                    />
                                                </div>
                                                {formik.touched.email && formik.errors.email && (
                                                    <div className={styles.errorMessage}>{formik.errors.email}</div>
                                                )}
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label htmlFor="cv" className={styles.formLabel}>
                                                    Curriculum Vitae (CV)
                                                </label>
                                                <div className={styles.fileInputWrapper}>
                                                    <input
                                                        id="cv"
                                                        name="cv"
                                                        type="file"
                                                        className={styles.fileInput}
                                                        accept=".pdf,.doc,.docx"
                                                        onChange={handleFileChange}
                                                    />
                                                    <div className={styles.customFileInput}>
                                                        <Upload className={styles.fileIcon} />
                                                        <span className={styles.fileName}>
                                                            {selectedFileName || "Seleccionar archivo (.pdf, .doc)"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label htmlFor="coverLetter" className={styles.formLabel}>
                                                Carta de presentación
                                            </label>
                                            <textarea
                                                id="coverLetter"
                                                name="coverLetter"
                                                className={styles.formTextarea}
                                                placeholder="Escribe una breve presentación sobre ti y por qué eres adecuado para este puesto..."
                                                value={formik.values.coverLetter}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                rows={5}
                                            />
                                        </div>

                                        <button type="submit" className={styles.submitButton} disabled={!formik.isValid || isSubmitting}>
                                            {isSubmitting ? "Enviando..." : "Aplicar ahora"}
                                            <Send size={18} className={styles.submitIcon} />
                                        </button>
                                    </form>
                                </motion.div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.main>
    )
}

const mapStateToProps = (state) => {
    return {
        workOffer: state.register.currentWorkOffer,
        workOfferPostulation: state.register.workOfferPostulation,
    }
}

export default connect(mapStateToProps, {
    getWorkOfferById,
    sendWorkOfferPostulation,
    resetWorkOfferPostulation,
})(WorkOfferById)
